import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { isLocalStorageAvailable } from '@vendasta/core';
import { Categories, ShoppingCartItemType } from '@vendasta/marketplace-packages';
import {
  CheckboxFilterField,
  FilterField,
  FilterSection,
  FilterService,
  Filters,
  TabbedFilterField,
} from '@vendasta/uikit';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';
import { LMI_CATEGORIES } from '../lmi-categories';
import { CategoryToItemListMap, StoreItem } from '../store-item';

export const STOREFRONT_FILTER_NAME = 'Store Filters';

export interface StorefrontCategory {
  id: string;
  name: string;
  packageIds: string[];
}

export interface InitialFilteredStoreItems {
  storeItemIds: string[];
  filterName: string;
}

@Component({
  selector: 'store-storefront',
  templateUrl: './storefront.component.html',
  styleUrls: ['./storefront.component.scss'],
  standalone: false,
})
export class StorefrontComponent implements OnInit, OnChanges {
  @Input() useCustomCase = false;
  @Input() showFilters = true;
  @Input() initialFilteredStoreItems: InitialFilteredStoreItems = null;
  @Input() showAllPrices = false;
  @Input() currencyCode: string;
  @Input() filtersDisabled = false;
  @Input()
  set categories(value: StorefrontCategory[]) {
    this.categories$$.next(value || []);
  }

  @Input()
  set categoryIdToStoreItemListMap(value: CategoryToItemListMap) {
    this.packagesMap$$.next(value || new Map<string, StoreItem[]>());
  }
  @Output() categorySelected = new EventEmitter<StorefrontCategory>();
  @Output() productSelected = new EventEmitter<{ id: string; type: ShoppingCartItemType }>();
  @Output() getDataForCategory = new EventEmitter<StorefrontCategory>();
  filters$: Observable<Filters>;
  filteredItems$: Observable<StoreItem[]>;
  showLMIFilter$: Observable<boolean>;
  private categories$$ = new BehaviorSubject<StorefrontCategory[]>([]);
  protected packagesMap$$ = new BehaviorSubject<CategoryToItemListMap>(new Map<string, StoreItem[]>());
  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private selectedCateoryId$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private lmiCategoryFilter: Filters;
  private filterByInitialStoreItemIds$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private filterService: FilterService) {}

  ngOnInit(): void {
    const lmiCategoryCheckboxList = LMI_CATEGORIES.map((category) => {
      return new CheckboxFilterField({
        name: category.name,
        id: category.categoryId,
        value: false,
        disableTranslations: true,
        condition: (item: { lmiCategories: string[] }): boolean => {
          let lmiCategories: string[] = [];
          if (item.lmiCategories) {
            lmiCategories = item.lmiCategories.map((cat) => {
              return Categories[cat].toLowerCase();
            });
          }
          return lmiCategories.indexOf(category.categoryId) > -1;
        },
      });
    });

    const lmiCategoryFilterSection = new FilterSection({
      title: 'FRONTEND.STORE.STOREFRONT_FILTERS.LMI_CATEGORY',
      type: 'or',
      fields: lmiCategoryCheckboxList,
    });

    this.lmiCategoryFilter = new Filters(STOREFRONT_FILTER_NAME, [lmiCategoryFilterSection]);

    const packageIdsCheckbox = new CheckboxFilterField({
      name: this.initialFilteredStoreItems ? this.initialFilteredStoreItems.filterName : '',
      id: 'package_id_checkbox',
      value:
        !!this.initialFilteredStoreItems && !!this.initialFilteredStoreItems.storeItemIds
          ? this.initialFilteredStoreItems.storeItemIds.length > 0
          : false,
    });

    const packageIdsFilterSection = new FilterSection({
      title: 'Store item IDs', // Shouldn't be seen
      type: 'and',
      fields: [packageIdsCheckbox],
    });
    packageIdsFilterSection.hideSelectors = true;

    this.filters$ = this.categories$$.pipe(
      map((categories) => {
        const tabbedFilterField = new TabbedFilterField({
          name: '',
          value: null,
          id: 'categorytab',
          options: categories,
          disableTranslations: true,
        });

        const filters = new Filters('FRONTEND.STORE.STOREFRONT_FILTERS.TITLE', [
          new FilterSection({
            title: 'FRONTEND.STORE.STOREFRONT_FILTERS.CATEGORIES',
            type: 'or',
            fields: [tabbedFilterField],
          }),
          lmiCategoryFilterSection,
          packageIdsFilterSection,
        ]);
        filters.id = STOREFRONT_FILTER_NAME.toLowerCase().replace(' ', '_');

        return this.showFilters ? filters : null;
      }),
    );

    // Only use the filter service if not in incognito
    if (isLocalStorageAvailable() && typeof window !== 'undefined') {
      this.filterService.setFilters(this.lmiCategoryFilter);

      const selectedLMIFilters$ = this.filterService.filters.fieldValuesObservable.pipe(
        startWith([]),
        map(() => {
          return this.lmiCategoryFilter.fields.filter((item: CheckboxFilterField) => item.value);
        }),
      );

      this.showLMIFilter$ = selectedLMIFilters$.pipe(
        filter((selectedLMIFilters) => !!selectedLMIFilters),
        take(1),
        map((selectedLMIFilters) => selectedLMIFilters.length > 0),
      );
    }

    this.filteredItems$ = combineLatest([
      this.packagesMap$$,
      this.searchTerm$$,
      this.selectedCateoryId$$,
      this.filterByInitialStoreItemIds$$,
    ]).pipe(
      map(([packages, searchTerm, selectedCategoryId, filterByInitialStoreItemIds]) => {
        let storeItems: StoreItem[] = [];
        if (packages.size) {
          storeItems = selectedCategoryId
            ? packages.get(selectedCategoryId)
            : packages.get(Array.from(packages.keys())[0]);
        }
        storeItems = storeItems ? this.lmiCategoryFilter.applyFilters(storeItems) : [];

        if (filterByInitialStoreItemIds) {
          storeItems = storeItems.filter((storeItem) =>
            this.initialFilteredStoreItems.storeItemIds.includes(storeItem.id),
          );
        }

        return searchTerm
          ? storeItems.filter((item) => item?.name?.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1)
          : storeItems;
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialFilteredStoreItems) {
      this.filterByInitialStoreItemIds$$.next(
        !!this.initialFilteredStoreItems && !!this.initialFilteredStoreItems.storeItemIds
          ? this.initialFilteredStoreItems.storeItemIds.length > 0
          : false,
      );
    }
  }

  public onSearchTermChanged(term: string): void {
    this.searchTerm$$.next(term);
  }

  public onFieldValueChanged(filterField: FilterField<any>): void {
    if (filterField.id === 'package_id_checkbox') {
      this.filterByInitialStoreItemIds$$.next(filterField.value);
      return;
    }
    if (!(filterField instanceof TabbedFilterField)) {
      return;
    }

    this.selectedCateoryId$$.next(filterField.value.id);
    this.categorySelected.emit(filterField.value as StorefrontCategory);

    const currentStoreItemsForSelectCategory = this.packagesMap$$.getValue().get(filterField.value.id);
    if (!(currentStoreItemsForSelectCategory && currentStoreItemsForSelectCategory.length)) {
      this.getDataForCategory.emit(filterField.value as StorefrontCategory);
    }
  }

  public storeItemClicked(item: StoreItem): void {
    this.productSelected.emit({ id: item.id, type: item.type });
  }
}
